import { useState } from "react";
import { Container, Typography, Box, Card, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText } from "@mui/material";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";

const BreakReminder = () => {
  const [breakSuggestions, setBreakSuggestions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  const breakActivities = [
    {
      name: "Stretch & Breathe",
      description: "Stand up and do some light stretching. Take deep breaths for 2 minutes.",
      duration: "3-5 min",
      icon: "🧘",
    },
    {
      name: "Walk Around",
      description: "Take a short walk around your room or office. Get some movement in.",
      duration: "5 min",
      icon: "🚶",
    },
    {
      name: "Drink Water",
      description: "Hydrate! Drink a glass of water to refresh yourself.",
      duration: "2 min",
      icon: "💧",
    },
    {
      name: "Eye Rest",
      description: "Look away from the screen. Focus on distant objects for eye strain relief.",
      duration: "2 min",
      icon: "👀",
    },
    {
      name: "Snack & Relax",
      description: "Enjoy a healthy snack and take a mental break.",
      duration: "5 min",
      icon: "🍎",
    },
    {
      name: "Quick Meditation",
      description: "Close your eyes and meditate. Focus on your breathing.",
      duration: "5 min",
      icon: "🧠",
    },
    {
      name: "Listen to Music",
      description: "Play your favorite song or podcast. Relax and enjoy.",
      duration: "5-10 min",
      icon: "🎵",
    },
    {
      name: "Social Break",
      description: "Chat with a colleague or friend. Social connection helps refresh the mind.",
      duration: "5 min",
      icon: "👥",
    },
  ];

  const handleShowSuggestions = () => {
    // Get random 3 suggestions
    const shuffled = breakActivities.sort(() => Math.random() - 0.5);
    setBreakSuggestions(shuffled.slice(0, 3));
    setOpenDialog(true);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        align="center"
        sx={{
          fontWeight: 900,
          fontFamily: "'Montserrat', sans-serif",
          background: "linear-gradient(90deg, #FF5722, #D84315)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textTransform: "uppercase",
          letterSpacing: "2px",
          mb: 4,
        }}
        gutterBottom
      >
        Break Suggestions
      </Typography>

      <Card sx={{ mb: 4, backgroundColor: "rgba(255, 87, 34, 0.1)", border: "2px solid #FF5722" }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <SelfImprovementIcon sx={{ fontSize: 40, color: "#FF5722" }} />
            <Box flex={1}>
              <Typography variant="h6">Time for a Break?</Typography>
              <Typography variant="body2" color="textSecondary">
                After completing a pomodoro session, take some time to refresh your mind and body.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={handleShowSuggestions}
            sx={{ background: "linear-gradient(45deg, #FF5722, #D84315)" }}
          >
            Get Break Suggestions
          </Button>
        </CardContent>
      </Card>

      {/* All Activities */}
      <Typography variant="h6" gutterBottom>
        All Break Activities
      </Typography>
      <Box>
        {breakActivities.map((activity, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Typography variant="h4">{activity.icon}</Typography>
                <Box flex={1}>
                  <Typography variant="h6">{activity.name}</Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {activity.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#FF5722", fontWeight: "bold" }}>
                    Duration: {activity.duration}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Suggestions Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>🎯 Suggested Break Activities</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <List>
            {breakSuggestions.map((activity, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemText
                    primary={`${activity.icon} ${activity.name}`}
                    secondary={activity.description}
                  />
                </ListItem>
              </Card>
            ))}
          </List>
          <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 2 }}>
            Take {Math.min(...breakSuggestions.map((a) => parseInt(a.duration)))} - {Math.max(...breakSuggestions.map((a) => parseInt(a.duration)))} minutes to refresh!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="contained">
            Thanks!
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BreakReminder;
